'use client';

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../AdminLayout';
import {
    ArrowLeftIcon,
    ArrowPathIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    BoltIcon,
    DocumentDuplicateIcon,
    PlusIcon,
    UserCircleIcon,
    TrashIcon
} from '@heroicons/react/24/outline';
import { useTenantsService, TenantDto } from '../api/tenants';
import { useMigrationService, MigrationArticleDto, MigrationProfileDto } from '../api/migration';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/common/Button';
import Select from '@/components/common/Select';
import FormInput from '@/components/common/form/FormInput';
import { Spinner } from '@/components/common/Spinner';

export default function AdminMigrationPage() {
    const tenantsApi = useTenantsService();
    const migrationApi = useMigrationService();
    const { addToast } = useToast();

    const [tenants, setTenants] = useState<TenantDto[]>([]);
    const [sourceTenantId, setSourceTenantId] = useState('');
    const [targetTenantId, setTargetTenantId] = useState('');

    const [sourceArticles, setSourceArticles] = useState<MigrationArticleDto[]>([]);
    const [sourceProfiles, setSourceProfiles] = useState<MigrationProfileDto[]>([]);
    const [targetProfiles, setTargetProfiles] = useState<MigrationProfileDto[]>([]);

    const [sourceProfileId, setSourceProfileId] = useState('');
    const [selectedArticleIds, setSelectedArticleIds] = useState<string[]>([]);
    const [selectedProfileId, setSelectedProfileId] = useState('');

    const [loading, setLoading] = useState(true);
    const [loadingArticles, setLoadingArticles] = useState(false);
    const [loadingSourceProfiles, setLoadingSourceProfiles] = useState(false);
    const [loadingProfiles, setLoadingProfiles] = useState(false);
    const [migrating, setMigrating] = useState(false);
    const [deleteSource, setDeleteSource] = useState(true);

    const [dryRunResult, setDryRunResult] = useState<any>(null);
    const [migrationProgress, setMigrationProgress] = useState<{current: number, total: number} | null>(null);
    const [migrationResults, setMigrationResults] = useState<any[]>([]);

    // Profile creation state
    const [showCreateProfile, setShowCreateProfile] = useState(false);
    const [newProfileName, setNewProfileName] = useState('');
    const [newProfileHandle, setNewProfileHandle] = useState('');
    const [isCreatingProfile, setIsCreatingProfile] = useState(false);

    useEffect(() => {
        const fetchTenants = async () => {
            try {
                const data = await tenantsApi.getTenants();
                setTenants(data);
            } catch (err: any) {
                addToast('Failed to fetch tenants', 'error');
            } finally {
                setLoading(false);
            }
        };
        fetchTenants();
    }, [tenantsApi, addToast]);

    useEffect(() => {
        if (sourceTenantId) {
            const fetchSourceData = async () => {
                setLoadingArticles(true);
                setLoadingSourceProfiles(true);
                try {
                    const [articlesData, profilesData] = await Promise.all([
                        migrationApi.getTenantArticles(sourceTenantId),
                        migrationApi.getTenantProfiles(sourceTenantId)
                    ]);
                    setSourceArticles(articlesData);
                    setSourceProfiles(profilesData);
                } catch (err) {
                    addToast('Failed to fetch source data', 'error');
                } finally {
                    setLoadingArticles(false);
                    setLoadingSourceProfiles(false);
                }
            };
            fetchSourceData();
        } else {
            setSourceArticles([]);
            setSourceProfiles([]);
        }
        setSourceProfileId('');
        setSelectedArticleIds([]);
        setMigrationResults([]);
        setDryRunResult(null);
    }, [sourceTenantId, migrationApi, addToast]);

    const filteredSourceArticles = sourceArticles.filter(a => a.profile_id === sourceProfileId);

    useEffect(() => {
        if (sourceProfileId) {
            setSelectedArticleIds(sourceArticles.filter(a => a.profile_id === sourceProfileId).map(a => a.id));
        } else {
            setSelectedArticleIds([]);
        }
    }, [sourceProfileId, sourceArticles]);

    const fetchProfiles = async () => {
        if (!targetTenantId) return;
        setLoadingProfiles(true);
        try {
            const data = await migrationApi.getTenantProfiles(targetTenantId);
            setTargetProfiles(data);
        } catch (err) {
            addToast('Failed to fetch target profiles', 'error');
        } finally {
            setLoadingProfiles(false);
        }
    };

    useEffect(() => {
        if (targetTenantId) {
            fetchProfiles();
        } else {
            setTargetProfiles([]);
        }
        setSelectedProfileId('');
        setShowCreateProfile(false);
    }, [targetTenantId, migrationApi, addToast]);

    const handleCreateProfile = async () => {
        if (!newProfileName || !newProfileHandle || !targetTenantId) {
            addToast('Please fill in all profile fields', 'error');
            return;
        }

        setIsCreatingProfile(true);
        try {
            const { user_id } = await migrationApi.getTenantOwner(targetTenantId);
            if (!user_id) throw new Error('Tenant owner not found');

            const profile = await migrationApi.createProfile(targetTenantId, {
                name: newProfileName,
                handle: newProfileHandle,
                userId: user_id
            });

            addToast('Profile created successfully', 'success');
            await fetchProfiles();
            setSelectedProfileId(profile.id);
            setShowCreateProfile(false);
            setNewProfileName('');
            setNewProfileHandle('');
        } catch (err: any) {
            addToast(err.message || 'Failed to create profile', 'error');
        } finally {
            setIsCreatingProfile(false);
        }
    };

    const handleMigrate = async (dryRun: boolean) => {
        if (selectedArticleIds.length === 0 || !targetTenantId || !selectedProfileId) {
            addToast('Please select source articles and target profile', 'error');
            return;
        }

        const targetProfile = targetProfiles.find(p => p.id === selectedProfileId);
        if (!targetProfile) return;

        setMigrating(true);
        setMigrationProgress({ current: 0, total: selectedArticleIds.length });
        setMigrationResults([]);
        setDryRunResult(null);
        
        const results = [];
        let hasError = false;

        for (let i = 0; i < selectedArticleIds.length; i++) {
            const articleId = selectedArticleIds[i];
            const originalArticle = sourceArticles.find(a => a.id === articleId);
            setMigrationProgress({ current: i + 1, total: selectedArticleIds.length });
            try {
                const result = await migrationApi.migrateArticle({
                    articleId,
                    targetTenantId,
                    targetAuthorId: targetProfile.user_id,
                    targetProfileId: selectedProfileId,
                    dryRun,
                    // @ts-ignore
                    deleteSource: !dryRun && deleteSource
                });
                results.push({ articleId, title: originalArticle?.title || 'Unknown', success: true, data: result });
            } catch (err: any) {
                hasError = true;
                results.push({ articleId, title: originalArticle?.title || 'Unknown', success: false, error: err.response?.data?.message || err.message || 'Migration failed' });
            }
        }

        setMigrationResults(results);

        if (dryRun) {
            addToast('Dry run completed', hasError ? 'error' : 'success');
        } else {
            addToast('Migration completed', hasError ? 'error' : 'success');
            // Refresh source articles
            const data = await migrationApi.getTenantArticles(sourceTenantId);
            setSourceArticles(data);
            setSelectedArticleIds([]);
        }
        
        setMigrating(false);
        setMigrationProgress(null);
    };

    if (loading) return (
        <AdminLayout>
            <div className="flex items-center justify-center min-h-[400px]">
                <Spinner size="lg" />
            </div>
        </AdminLayout>
    );

    return (
        <AdminLayout>
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <ArrowPathIcon className="h-8 w-8 text-pink-600 mr-3" />
                        <h1 className="text-2xl font-bold text-gray-900">Content Migration</h1>
                    </div>
                    <Link
                        to="/admin-tools"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                        <ArrowLeftIcon className="h-5 w-5 text-gray-500 mr-2" />
                        Back to Admin Tools
                    </Link>
                </div>
                <p className="mt-2 text-sm text-gray-600">Move articles and assets between accounts with ID preservation.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                    <div className="flex items-center mb-6">
                        <div className="bg-blue-100 p-2 rounded-lg mr-3">
                            <DocumentDuplicateIcon className="h-6 w-6 text-blue-600" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900">Source</h2>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Source Tenant</label>
                            <Select
                                value={sourceTenantId}
                                onChange={(val: string) => setSourceTenantId(val)}
                                options={[
                                    { label: 'Select Tenant...', value: '' },
                                    ...tenants.map(t => ({ label: `${t.name} (${t.id})`, value: t.id }))
                                ]}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Source Profile</label>
                            <div className="relative">
                                <Select
                                    value={sourceProfileId}
                                    onChange={(val: string) => {
                                        setSourceProfileId(val);
                                        setMigrationResults([]);
                                        setDryRunResult(null);
                                    }}
                                    disabled={!sourceTenantId || loadingSourceProfiles || (!loadingSourceProfiles && sourceProfiles.length === 0)}
                                    options={[
                                        { 
                                            label: loadingSourceProfiles 
                                                ? 'Loading profiles...' 
                                                : (!sourceTenantId ? 'Select Tenant first...' : (sourceProfiles.length === 0 ? 'No profiles found' : 'Select Profile...')), 
                                            value: '' 
                                        },
                                        ...sourceProfiles.map(p => ({ label: `${p.name} (@${p.handle})`, value: p.id }))
                                    ]}
                                />
                                {loadingSourceProfiles && (
                                    <div className="absolute right-8 top-1/2 -translate-y-1/2">
                                        <Spinner size="sm" />
                                    </div>
                                )}
                            </div>
                            {sourceTenantId && sourceProfiles.length === 0 && !loadingSourceProfiles && (
                                <p className="mt-1 text-xs text-yellow-600 flex items-center">
                                    <ExclamationTriangleIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                                    This tenant has no profiles to migrate from.
                                </p>
                            )}
                        </div>

                        {sourceProfileId && (
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-gray-700">Articles to Migrate</label>
                                    <span className="text-xs text-gray-500">{selectedArticleIds.length} of {filteredSourceArticles.length} selected</span>
                                </div>
                                <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-2 bg-gray-50">
                                    {loadingArticles ? (
                                        <div className="flex justify-center p-4"><Spinner size="sm" /></div>
                                    ) : filteredSourceArticles.length === 0 ? (
                                        <p className="text-sm text-gray-500 text-center py-4">No articles found for this profile.</p>
                                    ) : (
                                        filteredSourceArticles.map(article => (
                                            <label key={article.id} className="flex items-start bg-white p-2.5 rounded shadow-sm border border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors">
                                                <input 
                                                    type="checkbox" 
                                                    className="mt-1 flex-shrink-0 h-4 w-4 text-pink-600 focus:ring-pink-500 rounded border-gray-300"
                                                    checked={selectedArticleIds.includes(article.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedArticleIds(prev => [...prev, article.id]);
                                                        } else {
                                                            setSelectedArticleIds(prev => prev.filter(id => id !== article.id));
                                                        }
                                                        setMigrationResults([]);
                                                        setDryRunResult(null);
                                                    }}
                                                />
                                                <div className="ml-3 flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">{article.title || 'Untitled Article'}</p>
                                                    <p className="text-xs text-gray-500">{new Date(article.created_at).toLocaleDateString()} • {article.status}</p>
                                                </div>
                                            </label>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                    <div className="flex items-center mb-6 justify-between">
                        <div className="flex items-center">
                            <div className="bg-green-100 p-2 rounded-lg mr-3">
                                <CheckCircleIcon className="h-6 w-6 text-green-600" />
                            </div>
                            <h2 className="text-lg font-bold text-gray-900">Destination</h2>
                        </div>
                        {targetTenantId && !showCreateProfile && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowCreateProfile(true)}
                            >
                                <PlusIcon className="h-4 w-4 mr-1" />
                                New Profile
                            </Button>
                        )}
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Target Tenant</label>
                            <Select
                                value={targetTenantId}
                                onChange={(val: string) => setTargetTenantId(val)}
                                options={[
                                    { label: 'Select Tenant...', value: '' },
                                    ...tenants.map(t => ({ label: `${t.name} (${t.id})`, value: t.id }))
                                ]}
                            />
                        </div>

                        {showCreateProfile ? (
                            <div className="bg-gray-50 p-4 rounded-lg border border-dashed border-gray-300">
                                <div className="flex items-center mb-3">
                                    <UserCircleIcon className="h-5 w-5 text-gray-400 mr-2" />
                                    <span className="text-sm font-semibold">New Profile Setup</span>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <FormInput
                                        placeholder="Profile Name"
                                        value={newProfileName}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewProfileName(e.target.value)}
                                    />
                                    <FormInput
                                        placeholder="handle"
                                        value={newProfileHandle}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewProfileHandle(e.target.value)}
                                    />
                                </div>
                                <div className="flex justify-end gap-2 mt-3">
                                    <Button variant="ghost" size="sm" onClick={() => setShowCreateProfile(false)}>Cancel</Button>
                                    <Button size="sm" onClick={handleCreateProfile} disabled={isCreatingProfile}>
                                        {isCreatingProfile ? 'Creating...' : 'Create & Select'}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Target Profile</label>
                                <div className="relative">
                                    <Select
                                        value={selectedProfileId}
                                        onChange={(val: string) => {
                                            setSelectedProfileId(val);
                                            setDryRunResult(null);
                                        }}
                                        disabled={!targetTenantId || loadingProfiles || (!loadingProfiles && targetProfiles.length === 0)}
                                        options={[
                                            { 
                                                label: loadingProfiles 
                                                    ? 'Loading profiles...' 
                                                    : (!targetTenantId ? 'Select Tenant first...' : (targetProfiles.length === 0 ? 'No profiles found' : 'Select Profile...')), 
                                                value: '' 
                                            },
                                            ...targetProfiles.map(p => ({ label: `${p.name} (@${p.handle})`, value: p.id }))
                                        ]}
                                    />
                                    {loadingProfiles && (
                                        <div className="absolute right-8 top-1/2 -translate-y-1/2">
                                            <Spinner size="sm" />
                                        </div>
                                    )}
                                </div>
                                {targetTenantId && targetProfiles.length === 0 && !loadingProfiles && (
                                    <p className="mt-1 text-xs text-yellow-600 flex items-center">
                                        <ExclamationTriangleIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                                        This tenant has no profiles. Please create one above to proceed.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="mt-8 flex flex-col items-center">
                <div className="mb-6 w-full max-w-2xl bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <div className="flex items-start mb-3">
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-yellow-800">
                            <strong>Note:</strong> Original article content UUIDs will remain unchanged.
                            Articles will be associated with the selected target profile.
                        </p>
                    </div>
                    <div className="flex items-center space-x-2 pl-7 pt-2 border-t border-yellow-200/50">
                        <input
                            type="checkbox"
                            id="deleteSource"
                            checked={deleteSource}
                            onChange={(e) => setDeleteSource(e.target.checked)}
                            className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                        />
                        <label htmlFor="deleteSource" className="text-sm font-medium text-yellow-900 flex items-center cursor-pointer">
                            <TrashIcon className="h-4 w-4 mr-1 text-pink-600" />
                            Delete source article and asset files after successful migration
                        </label>
                    </div>
                </div>

                {migrating && migrationProgress && (
                    <div className="w-full max-w-2xl mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                        <div className="flex justify-between text-sm font-medium text-gray-700 mb-2">
                            <span>Processing Migrations...</span>
                            <span>{migrationProgress.current} of {migrationProgress.total}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                            <div className="bg-pink-600 h-2.5 rounded-full transition-all duration-300 ease-out" style={{ width: `${(migrationProgress.current / migrationProgress.total) * 100}%` }}></div>
                        </div>
                    </div>
                )}

                <div className="flex gap-4">
                    <Button
                        variant="secondary"
                        onClick={() => handleMigrate(true)}
                        disabled={selectedArticleIds.length === 0 || !selectedProfileId || migrating}
                    >
                        <BoltIcon className="h-5 w-5 mr-1" />
                        {migrating ? 'Processing...' : `Dry Run Preview (${selectedArticleIds.length} Articles)`}
                    </Button>
                    <Button
                        variant="default"
                        onClick={() => handleMigrate(false)}
                        disabled={selectedArticleIds.length === 0 || !selectedProfileId || migrating}
                        className="bg-pink-600 hover:bg-pink-700 text-white"
                    >
                        {migrating ? 'Migrating...' : `Execute Migration (${selectedArticleIds.length} Articles)`}
                    </Button>
                </div>

                {migrationResults.length > 0 && (
                    <div className="mt-8 w-full max-w-4xl bg-gray-900 rounded-xl p-6 overflow-hidden shadow-xl">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center">
                                <div className="h-2 w-2 rounded-full bg-green-500 mr-2 animate-pulse" />
                                <h3 className="text-sm font-bold tracking-wider text-white uppercase">Migration Results</h3>
                            </div>
                            <span className="text-xs bg-gray-800 text-gray-300 px-3 py-1 rounded-full">
                                {migrationResults.filter(r => r.success).length} Succeeded / {migrationResults.filter(r => !r.success).length} Failed
                            </span>
                        </div>
                        <div className="space-y-3 max-h-96 overflow-auto pr-2 custom-scrollbar">
                            {migrationResults.map((result, idx) => (
                                <div key={idx} className={`p-4 rounded-lg border flex flex-col ${result.success ? 'bg-gray-800 border-gray-700' : 'bg-red-900/20 border-red-800/50'}`}>
                                    <div className="flex justify-between items-start mb-2">
                                        <h4 className="text-sm font-medium text-gray-200 truncate mr-4">{result.title}</h4>
                                        <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium whitespace-nowrap ${result.success ? 'bg-emerald-900/50 text-emerald-400 border border-emerald-800' : 'bg-red-900/50 text-red-400 border border-red-800'}`}>
                                            {result.success ? 'Success' : 'Failed'}
                                        </span>
                                    </div>
                                    <pre className={`text-xs font-mono overflow-auto max-h-40 rounded p-2 ${result.success ? 'text-emerald-500/80 bg-black/20' : 'text-red-400 bg-red-950/30'}`}>
                                        {JSON.stringify(result.success ? result.data : result.error, null, 2)}
                                    </pre>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}
